import { useState } from "react";
import { Globe2, Database, Download, ExternalLink, ChevronRight } from "@/lib/icons";
import { Button } from "@/components/ui/button";
import { TOPIC_CASE_STUDIES } from "@/data/topicCaseStudies";
import { TOPIC_DATASETS, datasetToCsv } from "@/data/topicDatasets";

interface Props {
  topicId: string;
}

/**
 * "Chemistry in the World" case study + downloadable dataset for analysis.
 * Both pieces are bundled per topic; either may be missing for a given topic.
 */
export default function TopicApplications({ topicId }: Props) {
  const story = TOPIC_CASE_STUDIES[topicId];
  const dataset = TOPIC_DATASETS[topicId];
  const [openStory, setOpenStory] = useState(false);
  const [openData, setOpenData] = useState(false);

  if (!story && !dataset) return null;

  const downloadCsv = () => {
    if (!dataset) return;
    const csv = datasetToCsv(dataset);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = dataset.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-3 pt-3 border-t border-border space-y-2">
      {story && (
        <div className="rounded-md border border-border bg-tile-bg overflow-hidden">
          <button
            type="button"
            onClick={() => setOpenStory((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/40 transition-colors"
            aria-expanded={openStory}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Globe2 size={14} className="text-primary flex-shrink-0" aria-hidden="true" />
              <span className="text-xs font-semibold text-foreground uppercase tracking-wide">
                Chemistry in the world
              </span>
              <span className="text-[10px] text-muted-foreground truncate">
                {story.title}
              </span>
            </div>
            <ChevronRight
              size={12}
              className={`text-muted-foreground transition-transform flex-shrink-0 ${
                openStory ? "rotate-90" : ""
              }`}
              aria-hidden="true"
            />
          </button>

          {openStory && (
            <div className="px-3 pb-3 pt-1 space-y-2">
              <p className="text-sm font-semibold text-foreground">{story.title}</p>
              <p className="text-xs italic text-muted-foreground">{story.hook}</p>
              {story.body.map((p, i) => (
                <p key={i} className="text-xs text-foreground/90 leading-relaxed">
                  {p}
                </p>
              ))}
              <div className="rounded border border-primary/30 bg-primary/5 px-2.5 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-primary mb-0.5">
                  Takeaway
                </p>
                <p className="text-xs text-foreground">{story.takeaway}</p>
              </div>
              {story.source && (
                <p className="text-[11px] text-muted-foreground">
                  Source:{" "}
                  {story.source.url ? (
                    <a
                      href={story.source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary underline decoration-dotted underline-offset-2 inline-flex items-center gap-1"
                    >
                      {story.source.label}
                      <ExternalLink size={9} aria-hidden="true" />
                    </a>
                  ) : (
                    story.source.label
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {dataset && (
        <div className="rounded-md border border-border bg-tile-bg overflow-hidden">
          <button
            type="button"
            onClick={() => setOpenData((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted/40 transition-colors"
            aria-expanded={openData}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Database size={14} className="text-primary flex-shrink-0" aria-hidden="true" />
              <span className="text-xs font-semibold text-foreground uppercase tracking-wide">
                Data set
              </span>
              <span className="text-[10px] text-muted-foreground truncate">
                {dataset.title}
              </span>
            </div>
            <ChevronRight
              size={12}
              className={`text-muted-foreground transition-transform flex-shrink-0 ${
                openData ? "rotate-90" : ""
              }`}
              aria-hidden="true"
            />
          </button>

          {openData && (
            <div className="px-3 pb-3 pt-1 space-y-2">
              <p className="text-xs text-muted-foreground">{dataset.description}</p>
              <div className="rounded border border-primary/30 bg-primary/5 px-2.5 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-primary mb-0.5">
                  Your task
                </p>
                <p className="text-xs text-foreground">{dataset.task}</p>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-muted-foreground font-mono">
                  {dataset.rows.length - 1} rows · {dataset.filename}
                </span>
                <Button size="sm" className="h-7 text-xs gap-1" onClick={downloadCsv}>
                  <Download size={12} aria-hidden="true" />
                  Download CSV
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}